import React from 'react';
import camelCase from 'lodash/camelCase';
import slug from 'slug';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogProps, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';
import { PropertyType, createPropertyInput, CreatePropertyInput } from '@producer/properties';

import { PropertyTypeSelector } from './property-type-selector';
import { Project } from '../../../app/entities';
import { Property } from '../../../app/entities/property';

import classes from './save-property-modal.module.scss';

type SavePropertyModalProps = DialogProps & {
  title: string;
  project?: Project;
  onCancel: () => void;
};

export const CreatePropertyModal: React.FC<SavePropertyModalProps> = ({
  title,
  project,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<CreatePropertyInput>({
    mode: 'onChange',
    resolver: zodResolver(
      createPropertyInput.refine(
        (data) => {
          // Generate the key using the same logic as backend
          const generatedKey = camelCase(slug(data.name, { lower: true }));

          // Check if the key already exists in project properties
          const propertyExists = project?.properties?.some(
            (property) => property.key === generatedKey || property.name === data.name,
          );

          // Return true if key doesn't exist (validation passes)
          return !propertyExists;
        },
        {
          message: 'A property with this name already exists',
          path: ['name'], // This targets the error to the name field
        },
      ),
    ),
    defaultValues: {
      name: '',
      type: PropertyType.SmallText,
      projectId: project._id,
    },
  });

  const onSubmit = async (input: CreatePropertyInput) => {
    for (const key in input) {
      if (input[key] === '') delete input[key];
    }

    await Property.create(input);

    onCancel();
  };

  return (
    <Dialog
      title={title}
      onClose={onCancel}
      actions={[
        <TextButton key={'cancel'} onClick={onCancel} text={'Cancel'} />,
        <ElevatedButton
          form="create-property-form"
          key="create"
          text={'Create'}
          isLoading={isSubmitting}
          disabled={!isValid}
        />,
      ]}
    >
      <form id="create-property-form" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          label="Property name"
          placeholder="Select a type"
          required
          {...register('name')}
          errorMessage={errors.name?.message}
        />

        <div className={classes.InputWrapper}>
          <label>
            Property type<b>*</b>
          </label>

          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, value } }) => (
              <PropertyTypeSelector onChange={onChange} value={value} />
            )}
          />
        </div>
      </form>
    </Dialog>
  );
};
